/**
 * The catalogue of icons that can be set on a view to override the icon of its
 * view type in the view switcher.
 *
 * Each entry is an `iconoir` icon name without the `iconoir-` class prefix, so a
 * picker renders one as `iconoir-<name>`.
 *
 * DO NOT EDIT BY HAND. This file mirrors `VIEW_ICONS` in
 * `backend/src/baserow/contrib/database/views/constants.py`, which is the single
 * source of truth and the list the API validates against. The two are kept
 * identical by `backend/tests/baserow/contrib/database/view/test_view_icons.py`,
 * which fails the build if they drift -- including on ORDER, so the groups below
 * must stay in the order the Python list is written in.
 *
 * The grouping lives here rather than in the backend because it exists purely
 * for the picker: the API only ever validates membership and has no interest in
 * which section an icon was rendered under. `VIEW_ICONS` is derived from the
 * groups so there is one list, not two that can disagree.
 *
 * `key` is an i18n key rather than a label, resolved against
 * `viewIdentity.iconGroup.*` -- see `ViewIdentityContext.vue`.
 */
export const VIEW_ICON_GROUPS = [
  {
    key: 'status',
    icons: [
      'check-circle',
      'check',
      'cancel',
      'warning-triangle',
      'warning-circle',
      'clock',
      'hourglass',
      'timer',
      'play',
      'pause',
      'fast-arrow-right',
      'refresh-double',
      'undo',
      'redo',
      'shield-check',
      'shield-alert',
      'prohibition',
      'circle',
      'half-moon',
      'triangle-flag',
      'hammer',
      'lock',
      'open-select-hand-gesture',
    ],
  },
  {
    key: 'people',
    icons: [
      'user',
      'group',
      'user-star',
      'user-crown',
      'user-love',
      'user-cart',
      'community',
      'profile-circle',
      'peace-hand',
      'handbag',
      'male',
      'female',
      'accessibility',
      'user-square',
      'add-user',
      'remove-user',
      'user-bag',
    ],
  },
  {
    key: 'communication',
    icons: [
      'mail',
      'send-mail',
      'phone',
      'phone-outcome',
      'phone-income',
      'headset-help',
      'bell',
      'bell-off',
      'chat-bubble',
      'chat-lines',
      'megaphone',
      'voice',
      'at-sign',
      'forward',
      'reply',
      'share-android',
    ],
  },
  {
    key: 'commerce',
    icons: [
      'shop',
      'cart',
      'coin',
      'coins',
      'money-square',
      'credit-card',
      'wallet',
      'gift',
      'delivery-truck',
      'package',
      'bank',
      'dollar',
      'euro',
      'percentage',
      'shopping-bag',
      'cash',
      'box-iso',
    ],
  },
  {
    key: 'media',
    icons: [
      'camera',
      'video-camera',
      'media-image',
      'media-video',
      'music-note',
      'music-double-note',
      'headset',
      'tv',
      'palette',
      'crop',
      'frame',
      'design-nib',
    ],
  },
  {
    key: 'files',
    icons: [
      'page',
      'page-edit',
      'multiple-pages',
      'folder',
      'book',
      'bookmark-book',
      'archive',
      'attachment',
      'printer',
      'notes',
      'journal',
      'clipboard-check',
      'copy',
      'doc-star',
      'empty-page',
      'binocular',
      'open-book',
      'post',
    ],
  },
  {
    key: 'data',
    icons: [
      'db',
      'graph-up',
      'graph-down',
      'stats-report',
      'reports',
      'percentage-circle',
      'dashboard-speed',
      'table',
      'table-2-columns',
      'activity',
      'calculator',
      'sigma-function',
      'coins-swap',
      'flare',
      'scan-barcode',
      'path-arrow',
    ],
  },
  {
    key: 'organisation',
    icons: [
      'filter',
      'sort',
      'sort-up',
      'sort-down',
      'search',
      'list',
      'view-grid',
      'view-columns-3',
      'label',
      'bookmark-empty',
      'pin',
      'folder-settings',
      'priority-up',
      'priority-down',
      'frame-alt',
      'layout-left',
      'menu',
    ],
  },
  {
    key: 'emphasis',
    icons: [
      'star',
      'heart',
      'fire-flame',
      'flash',
      'three-stars',
      'trophy',
      'medal',
      'crown',
      'sparks',
      'thumbs-up',
      'thumbs-down',
      'rocket',
      'magic-wand',
      'light-bulb',
      'sun-light',
      'favourite-book',
      'emoji',
    ],
  },
  {
    key: 'time',
    icons: [
      'calendar',
      'calendar-plus',
      'calendar-minus',
      'clock-rotate-right',
      'alarm',
      'timer-off',
      'planet',
      'moon-sat',
      'bell-notification',
    ],
  },
  {
    key: 'places',
    icons: [
      'home',
      'home-alt',
      'building',
      'map',
      'map-issue',
      'globe',
      'pin-alt',
      'navigator',
      'city',
      'garage',
      'airplane',
      'car',
      'bicycle',
      'walking',
      'sea-waves',
      'tree',
      'farm',
    ],
  },
  {
    key: 'tools',
    icons: [
      'tools',
      'wrench',
      'settings',
      'code',
      'terminal',
      'cpu',
      'server-connection',
      'cloud',
      'link',
      'key-alt',
      'ev-plug',
      'puzzle',
      'flask',
      'learning',
      'git-merge',
      'network',
      'plug-type-a',
      'database-script',
    ],
  },
]

/**
 * Every catalogue icon, flattened, in group order. This is the list the API
 * validates against and the one the sync test compares.
 */
export const VIEW_ICONS = [
  'check-circle',
  'check',
  'cancel',
  'warning-triangle',
  'warning-circle',
  'clock',
  'hourglass',
  'timer',
  'play',
  'pause',
  'fast-arrow-right',
  'refresh-double',
  'undo',
  'redo',
  'shield-check',
  'shield-alert',
  'prohibition',
  'circle',
  'half-moon',
  'triangle-flag',
  'hammer',
  'lock',
  'open-select-hand-gesture',
  'user',
  'group',
  'user-star',
  'user-crown',
  'user-love',
  'user-cart',
  'community',
  'profile-circle',
  'peace-hand',
  'handbag',
  'male',
  'female',
  'accessibility',
  'user-square',
  'add-user',
  'remove-user',
  'user-bag',
  'mail',
  'send-mail',
  'phone',
  'phone-outcome',
  'phone-income',
  'headset-help',
  'bell',
  'bell-off',
  'chat-bubble',
  'chat-lines',
  'megaphone',
  'voice',
  'at-sign',
  'forward',
  'reply',
  'share-android',
  'shop',
  'cart',
  'coin',
  'coins',
  'money-square',
  'credit-card',
  'wallet',
  'gift',
  'delivery-truck',
  'package',
  'bank',
  'dollar',
  'euro',
  'percentage',
  'shopping-bag',
  'cash',
  'box-iso',
  'camera',
  'video-camera',
  'media-image',
  'media-video',
  'music-note',
  'music-double-note',
  'headset',
  'tv',
  'palette',
  'crop',
  'frame',
  'design-nib',
  'page',
  'page-edit',
  'multiple-pages',
  'folder',
  'book',
  'bookmark-book',
  'archive',
  'attachment',
  'printer',
  'notes',
  'journal',
  'clipboard-check',
  'copy',
  'doc-star',
  'empty-page',
  'binocular',
  'open-book',
  'post',
  'db',
  'graph-up',
  'graph-down',
  'stats-report',
  'reports',
  'percentage-circle',
  'dashboard-speed',
  'table',
  'table-2-columns',
  'activity',
  'calculator',
  'sigma-function',
  'coins-swap',
  'flare',
  'scan-barcode',
  'path-arrow',
  'filter',
  'sort',
  'sort-up',
  'sort-down',
  'search',
  'list',
  'view-grid',
  'view-columns-3',
  'label',
  'bookmark-empty',
  'pin',
  'folder-settings',
  'priority-up',
  'priority-down',
  'frame-alt',
  'layout-left',
  'menu',
  'star',
  'heart',
  'fire-flame',
  'flash',
  'three-stars',
  'trophy',
  'medal',
  'crown',
  'sparks',
  'thumbs-up',
  'thumbs-down',
  'rocket',
  'magic-wand',
  'light-bulb',
  'sun-light',
  'favourite-book',
  'emoji',
  'calendar',
  'calendar-plus',
  'calendar-minus',
  'clock-rotate-right',
  'alarm',
  'timer-off',
  'planet',
  'moon-sat',
  'bell-notification',
  'home',
  'home-alt',
  'building',
  'map',
  'map-issue',
  'globe',
  'pin-alt',
  'navigator',
  'city',
  'garage',
  'airplane',
  'car',
  'bicycle',
  'walking',
  'sea-waves',
  'tree',
  'farm',
  'tools',
  'wrench',
  'settings',
  'code',
  'terminal',
  'cpu',
  'server-connection',
  'cloud',
  'link',
  'key-alt',
  'ev-plug',
  'puzzle',
  'flask',
  'learning',
  'git-merge',
  'network',
  'plug-type-a',
  'database-script',
]

export default VIEW_ICONS
